/**
 * RowManager — encapsulates row CRUD operations that were previously
 * inline in the DataGridElement class.
 *
 * Owns:
 * - Row ID resolution (tryResolveRowId, resolveRowIdOrThrow)
 * - Row lookup (getRow)
 * - Row mutation (updateRow, updateRows, insertRow, removeRow)
 *
 * Takes the grid reference directly (tightly coupled — this manager
 * can never live outside the grid).
 */
import type { CellChangeDetail, GridHost, RowTransaction, TransactionResult, UpdateSource } from '../types';
import { MISSING_ROW_ID, ROW_NOT_FOUND, throwDiagnostic, warnDiagnostic } from './diagnostics';
import { RenderPhase } from './render-scheduler';
import { animateRow } from './row-animation';
import { invalidateCellCache } from './rows';
import { invalidateAccessorCache, readCellField, writeCellField } from './value-accessor';

// #region Standalone Row ID Helpers

/**
 * Try to resolve the ID for a row using a configured getRowId or fallback.
 * Returns undefined if no ID can be determined (non-throwing).
 *
 * Exported so grid.ts can use it in `#rebuildRowIdMap` without going
 * through the RowManager instance.
 */
export function tryResolveRowId<T>(row: T, getRowId?: (row: T) => string): string | undefined {
  const r = row as Record<string, unknown>;
  // Plugin loading placeholders (ServerSide blocks / GroupingRows groups in flight) are
  // not consumer rows — give them a stable internal id instead of calling `getRowId`.
  if (r?.__loading === true) {
    if (r.__index != null) return `__loading:${r.__index}`;
    if (r.__groupKey != null) return `__loading:group:${r.__groupKey}`;
    return undefined;
  }

  if (getRowId) {
    return getRowId(row);
  }

  // Fallback: common ID fields
  if ('id' in r && r.id != null) return String(r.id);
  if ('_id' in r && r._id != null) return String(r._id);

  return undefined;
}

/**
 * Resolve the ID for a row, throwing if not found.
 * Exported so grid.ts `getRowId()` can call it without the RowManager.
 */
export function resolveRowIdOrThrow<T>(row: T, gridId: string, getRowId?: (row: T) => string): string {
  const id = tryResolveRowId(row, getRowId);
  if (id === undefined) {
    throwDiagnostic(
      MISSING_ROW_ID,
      'Cannot determine row ID. ' + 'Configure getRowId in gridConfig or ensure rows have an "id" property.',
      gridId,
    );
  }
  return id;
}

// #endregion

// #region RowManager

export class RowManager<T = any> {
  readonly #grid: GridHost<T>;

  constructor(grid: GridHost<T>) {
    this.#grid = grid;
  }

  // --- Row lookup ---

  getRow(id: string): T | undefined {
    return this.#grid._getRowEntry(id)?.row;
  }

  // --- Row updates ---

  /**
   * Apply a set of field changes to a single already-resolved row, routing each
   * change through data plugins (e.g. editing) so validation, dirty tracking,
   * history, and abortion all participate.
   *
   * Per-field plugin response contract (`commitCellValue` query):
   * - `false` → a plugin vetoed the change (validation/abortion); skip the field.
   * - `true` → a plugin applied AND tracked the value itself; core must not re-apply.
   * - _(no response)_ → not handled; core applies the value in place (legacy path).
   *
   * Emits `cell-change` for each field that actually changed and returns the list
   * so callers can decide whether to schedule a render.
   */
  #applyRowChanges(
    id: string,
    row: T,
    index: number,
    changes: Partial<T>,
    source: UpdateSource,
  ): Array<{ field: string; oldValue: unknown; newValue: unknown }> {
    const grid = this.#grid;
    const changedFields: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];

    for (const [field, newValue] of Object.entries(changes)) {
      // Guard against prototype-pollution keys before any read/query/write.
      if (field === '__proto__' || field === 'constructor' || field === 'prototype') continue;

      const oldValue = readCellField(row, field);
      if (oldValue === newValue) continue;

      const responses = grid.query?.<boolean>('commitCellValue', {
        rowIndex: index,
        rowId: id,
        row,
        field,
        oldValue,
        newValue,
        source,
      });
      // A veto (`false`) from ANY plugin wins regardless of response order; a
      // `true` means a plugin applied AND tracked the value itself; otherwise
      // (no boolean response) core applies the value directly.
      if (responses?.includes(false)) continue; // vetoed (validation/abortion)

      if (!responses?.includes(true)) {
        // No data plugin handled it — apply in place (legacy behavior).
        writeCellField(row, field, newValue);
        // Invalidate the WHOLE row's accessor cache, not just (row, field): a
        // `valueAccessor` can read ANY property (e.g. field `deal.comments`
        // reading `row.dealComment`), so the cache entry — keyed by
        // `column.field` — need not match the mutated property name.
        invalidateAccessorCache(row as object);
      }
      // else: a data plugin already applied (and tracked) the value.

      changedFields.push({ field, oldValue, newValue });

      grid.dispatchEvent(
        new CustomEvent('cell-change', {
          detail: {
            row,
            rowId: id,
            rowIndex: index,
            field,
            oldValue,
            newValue,
            changes,
            source,
          } as CellChangeDetail<T>,
          bubbles: true,
          composed: true,
        }),
      );
    }

    return changedFields;
  }

  updateRow(id: string, changes: Partial<T>, source: UpdateSource = 'api'): void {
    const grid = this.#grid;
    // Resolve from the full source dataset — not just the visible `_rows` — so a
    // row that is filtered/paged out of view can still be updated.
    const entry = grid._getSourceRowEntry(id);
    if (!entry) {
      // The row exists in neither the view nor the source data. Warn instead of
      // throwing so a single stale ID can never crash the grid. Undo/redo replays
      // ('history') legitimately outlive deleted rows, so they skip silently.
      if (source !== 'history') {
        warnDiagnostic(
          ROW_NOT_FOUND,
          `Row with ID "${id}" not found. ` + `Ensure the row exists and getRowId is correctly configured.`,
          grid.id,
        );
      }
      return;
    }

    const { row, index } = entry;
    const changedFields = this.#applyRowChanges(id, row, index, changes, source);

    // Schedule re-render if anything changed.
    // Use VIRTUALIZATION (not ROWS) so the visible cells are re-rendered
    // without rebuilding the row model. A ROWS-phase rebuild re-applies
    // sort/filter from #rows, which moves rows inserted via insertRow()
    // to their sorted position — appearing as "ghost" duplicates.
    // Since data was already mutated in-place, fastPatchRow will pick up
    // the new values from the row object directly.
    if (changedFields.length > 0) {
      invalidateCellCache(grid);
      grid._requestSchedulerPhase(RenderPhase.VIRTUALIZATION, 'updateRow');
      grid._emitDataChange();
    }
  }

  updateRows(updates: Array<{ id: string; changes: Partial<T> }>, source: UpdateSource = 'api'): void {
    const grid = this.#grid;
    let anyChanged = false;

    for (const { id, changes } of updates) {
      // Resolve from the full source dataset so filtered/paged-out rows update too.
      const entry = grid._getSourceRowEntry(id);
      if (!entry) {
        // Warn and skip this row instead of throwing — the rest of the batch
        // (and the grid) must survive an unresolvable ID. 'history' skips silently.
        if (source !== 'history') {
          warnDiagnostic(
            ROW_NOT_FOUND,
            `Row with ID "${id}" not found. ` + `Ensure the row exists and getRowId is correctly configured.`,
            grid.id,
          );
        }
        continue;
      }

      const { row, index } = entry;
      const changedFields = this.#applyRowChanges(id, row, index, changes, source);
      if (changedFields.length > 0) anyChanged = true;
    }

    // Schedule single re-render for all changes.
    // Use VIRTUALIZATION (not ROWS) — see updateRow for rationale.
    if (anyChanged) {
      invalidateCellCache(grid);
      grid._requestSchedulerPhase(RenderPhase.VIRTUALIZATION, 'updateRows');
      grid._emitDataChange();
    }
  }

  // --- Row mutation ---

  async insertRow(index: number, row: T, animate = true): Promise<void> {
    const grid = this.#grid;

    // Clamp index to valid range
    const idx = Math.max(0, Math.min(index, grid._rows.length));

    // Add to source data (position irrelevant — pipeline will re-sort later)
    grid.sourceRows = [...grid.sourceRows, row];

    // Insert into processed view at the exact visible position
    const newRows = [...grid._rows];
    newRows.splice(idx, 0, row);
    grid._rows = newRows;

    // Keep __originalOrder in sync so "clear sort" includes the new row
    if (grid._sortState) {
      grid.__originalOrder = [...grid.__originalOrder, row];
    }

    // Refresh caches and trigger immediate re-render
    this.#invalidateAndRerender();

    // Notify plugins about the inserted row (e.g., editing dirty tracking)
    grid._emitPluginEvent('row-inserted', { row, index: idx });

    grid._emitDataChange();

    if (animate) {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await animateRow(grid, idx, 'insert');
    }
  }

  async removeRow(index: number, animate = true): Promise<T | undefined> {
    const grid = this.#grid;
    const row = grid._rows[index];
    if (!row) return undefined;

    if (animate) {
      await animateRow(grid, index, 'remove');
    }

    // Find current position by reference (may have shifted during animation)
    const currentIdx = grid._rows.indexOf(row);
    if (currentIdx < 0) return row; // Already removed by something else

    // Remove from processed view
    const newRows = [...grid._rows];
    newRows.splice(currentIdx, 1);
    grid._rows = newRows;

    // Remove from source data
    const srcIdx = grid.sourceRows.indexOf(row);
    if (srcIdx >= 0) {
      const newSource = [...grid.sourceRows];
      newSource.splice(srcIdx, 1);
      grid.sourceRows = newSource;
    }

    // Keep __originalOrder in sync
    if (grid._sortState) {
      const origIdx = grid.__originalOrder.indexOf(row);
      if (origIdx >= 0) {
        const newOrig = [...grid.__originalOrder];
        newOrig.splice(origIdx, 1);
        grid.__originalOrder = newOrig;
      }
    }

    // Refresh caches and trigger immediate re-render
    this.#invalidateAndRerender();

    grid._emitDataChange();

    // Clean up stale remove animation attributes after re-render
    if (animate) this.#clearRemoveAnimations();

    return row;
  }

  // --- Transaction API ---

  async applyTransaction(transaction: RowTransaction<T>, animate = true): Promise<TransactionResult<T>> {
    const result: TransactionResult<T> = { added: [], updated: [], removed: [] };

    // 1. Process removes first (before indices shift from inserts)
    await this.#txRemove(transaction, result, animate);
    // 2. Process updates (in-place mutation, no structural change).
    //    Removed IDs are skipped so updates don't target removed rows.
    this.#txUpdate(transaction, result);
    // 3. Process adds (append to end)
    this.#txAdd(transaction, result);
    // 4. Single render pass for all mutations
    this.#txRender(result);
    // 5-7. Animations + stale-attribute cleanup
    if (animate) await this.#txAnimate(result);

    return result;
  }

  /** Transaction phase 1 — remove rows from `_rows`, `sourceRows` and `__originalOrder`. */
  async #txRemove(transaction: RowTransaction<T>, result: TransactionResult<T>, animate: boolean): Promise<void> {
    if (!transaction.remove?.length) return;
    const grid = this.#grid;

    for (const { id } of transaction.remove) {
      const entry = grid._getRowEntry(id);
      if (!entry) continue;

      const { row } = entry;

      if (animate) {
        const idx = grid._rows.indexOf(row);
        if (idx >= 0) await animateRow(grid, idx, 'remove');
      }

      // Find current position (may have shifted during animation)
      const currentIdx = grid._rows.indexOf(row);
      if (currentIdx < 0) {
        result.removed.push(row);
        continue;
      }

      const newRows = [...grid._rows];
      newRows.splice(currentIdx, 1);
      grid._rows = newRows;

      const srcIdx = grid.sourceRows.indexOf(row);
      if (srcIdx >= 0) {
        const newSource = [...grid.sourceRows];
        newSource.splice(srcIdx, 1);
        grid.sourceRows = newSource;
      }

      if (grid._sortState) {
        const origIdx = grid.__originalOrder.indexOf(row);
        if (origIdx >= 0) {
          const newOrig = [...grid.__originalOrder];
          newOrig.splice(origIdx, 1);
          grid.__originalOrder = newOrig;
        }
      }

      result.removed.push(row);
    }
  }

  /** Transaction phase 2 — in-place field writes, one `cell-change` per changed field. */
  #txUpdate(transaction: RowTransaction<T>, result: TransactionResult<T>): void {
    if (!transaction.update?.length) return;
    const grid = this.#grid;
    const removedIds = new Set(transaction.remove?.map((r) => r.id));

    for (const { id, changes } of transaction.update) {
      if (removedIds.has(id)) continue;
      const entry = grid._getRowEntry(id);
      if (!entry) continue;

      const { row, index } = entry;
      let changed = false;

      for (const [field, newValue] of Object.entries(changes)) {
        const oldValue = readCellField(row, field);
        if (oldValue === newValue) continue;

        changed = true;
        writeCellField(row, field, newValue);
        // Whole-row invalidation: a `valueAccessor` may read a property
        // other than `column.field`, so per-field invalidation can miss
        // the stale cache entry keyed by `column.field`.
        invalidateAccessorCache(row as object);

        grid.dispatchEvent(
          new CustomEvent('cell-change', {
            detail: {
              row,
              rowId: id,
              rowIndex: index,
              field,
              oldValue,
              newValue,
              changes,
              source: 'api',
            } as CellChangeDetail<T>,
            bubbles: true,
            composed: true,
          }),
        );
      }

      if (changed) result.updated.push(row);
    }
  }

  /** Transaction phase 3 — append new rows to `sourceRows`, `_rows` and `__originalOrder`. */
  #txAdd(transaction: RowTransaction<T>, result: TransactionResult<T>): void {
    if (!transaction.add?.length) return;
    const grid = this.#grid;

    for (const row of transaction.add) {
      grid.sourceRows = [...grid.sourceRows, row];
      grid._rows = [...grid._rows, row];
      if (grid._sortState) grid.__originalOrder = [...grid.__originalOrder, row];
      result.added.push(row);
    }
  }

  /** Transaction phase 4 — one render pass covering every mutation in the batch. */
  #txRender(result: TransactionResult<T>): void {
    const grid = this.#grid;
    const hasStructuralChange = result.added.length > 0 || result.removed.length > 0;
    const hasUpdates = result.updated.length > 0;

    if (hasStructuralChange) {
      this.#invalidateAndRerender();
    } else if (hasUpdates) {
      invalidateCellCache(grid);
      grid._requestSchedulerPhase(RenderPhase.VIRTUALIZATION, 'applyTransaction');
    }

    if (hasStructuralChange || hasUpdates) grid._emitDataChange();
  }

  /** Transaction phases 5-7 — insert/change animations and stale remove-attribute cleanup. */
  async #txAnimate(result: TransactionResult<T>): Promise<void> {
    const grid = this.#grid;

    if (result.added.length > 0) {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      for (const row of result.added) {
        const idx = grid._rows.indexOf(row);
        if (idx >= 0) await animateRow(grid, idx, 'insert');
      }
    }

    for (const row of result.updated) {
      const idx = grid._rows.indexOf(row);
      if (idx >= 0) await animateRow(grid, idx, 'change');
    }

    if (result.removed.length > 0) this.#clearRemoveAnimations();
  }

  /**
   * Structural mutation aftermath: drop caches, rebuild the id map, bump the
   * render epoch so every pooled row is rebuilt, and re-render immediately.
   */
  #invalidateAndRerender(): void {
    const grid = this.#grid;
    invalidateCellCache(grid);
    grid._rebuildRowIdMap();
    grid.__rowRenderEpoch++;
    for (let i = 0; i < grid._rowPool.length; i++) grid._rowPool[i].__epoch = -1;
    grid.refreshVirtualWindow(true);
  }

  /** Drop `data-animating="remove"` markers left behind after the re-render. */
  #clearRemoveAnimations(): void {
    const grid = this.#grid;
    requestAnimationFrame(() => {
      const els = grid.querySelectorAll('[data-animating="remove"]');
      for (let i = 0; i < els.length; i++) els[i].removeAttribute('data-animating');
    });
  }

  #pendingTransaction: RowTransaction<T> | null = null;
  #pendingTransactionResolvers: Array<(result: TransactionResult<T>) => void> = [];
  #transactionRafId: number | null = null;

  applyTransactionAsync(transaction: RowTransaction<T>): Promise<TransactionResult<T>> {
    // Merge into pending batch
    if (!this.#pendingTransaction) {
      this.#pendingTransaction = { add: [], update: [], remove: [] };
    }
    if (transaction.add) this.#pendingTransaction.add!.push(...transaction.add);
    if (transaction.update) this.#pendingTransaction.update!.push(...transaction.update);
    if (transaction.remove) this.#pendingTransaction.remove!.push(...transaction.remove);

    return new Promise<TransactionResult<T>>((resolve) => {
      this.#pendingTransactionResolvers.push(resolve);

      if (this.#transactionRafId === null) {
        this.#transactionRafId = requestAnimationFrame(() => {
          this.#transactionRafId = null;
          const batch = this.#pendingTransaction!;
          const resolvers = this.#pendingTransactionResolvers;
          this.#pendingTransaction = null;
          this.#pendingTransactionResolvers = [];

          // Apply batched transaction without per-row animation (too many)
          this.applyTransaction(batch, false).then((result) => {
            for (const resolver of resolvers) resolver(result);
          });
        });
      }
    });
  }
}

// #endregion
